/** The add-a-record chooser, the About page, and Not found. */
import { Link } from 'react-router'
import { ChevronRight } from 'lucide-react'
import { ReadingPage } from '../components/ReadingPage'
import { signInHref, useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'

const KINDS = [
  {
    to: '/series/new',
    title: 'A series or setting',
    text: 'A campaign setting, product line, adventure series or collection.',
  },
  {
    to: '/systems/new',
    title: 'A system',
    text: 'A game or one edition of it, RPG or wargame: D&D 5e, Mothership, Warhammer 40,000 10th edition.',
    textWithoutWargames: 'A game or one edition of it: D&D 5e, Mothership, Call of Cthulhu 7th edition.',
  },
  {
    to: '/books/new',
    title: 'A book',
    text: 'A rulebook, supplement, adventure, zine or sheet. It can belong to several systems.',
  },
  {
    to: '/publishers/new',
    title: 'A publisher',
    text: 'A company or imprint. Usually created for you when you name one on a book.',
  },
  {
    to: '/creators/new',
    title: 'A creator',
    text: 'An author, artist, editor or designer. Also created for you from a book’s credits.',
  },
]

export function NewRecord() {
  useTitle('Add a record')
  const session = useSession()
  return (
    <div className="page narrow">
      <h1 className="page-title">Add a record</h1>
      <p className="lede">
        Search first, so the database does not end up with two of the same thing. Then pick what you are adding.
      </p>
      <ul className="kind-list">
        {KINDS.map((k) => (
          <li key={k.to}>
            <Link to={session.user ? k.to : signInHref(k.to)} className="kind">
              <span className="kind-title">{k.title}</span>
              <span className="kind-text">{session.wargames ? k.text : (k.textWithoutWargames ?? k.text)}</span>
              <ChevronRight className="kind-go" size={18} aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function About() {
  useTitle('About')
  const { wargames } = useSession()
  return (
    <ReadingPage>
      <h1 className="page-title">About GrimoireCodexDB</h1>
      <p className="lede">
        GrimoireCodexDB is a shared catalogue of tabletop RPG{wargames && ' and wargame'} metadata: systems, books,
        settings and the people who make them. It is maintained by its users, in the spirit of MusicBrainz and TMDB, and
        is the metadata source for the self-hosted{' '}
        <a href="https://github.com/hunter-read/grimoire" target="_blank" rel="noopener noreferrer">
          Grimoire
        </a>{' '}
        library manager{wargames && ' and for Codex, its counterpart for wargaming'}.
      </p>

      <h2 id="metadata-only">Metadata, never content</h2>
      <p>
        GrimoireCodexDB describes products; it never hosts them. No PDFs, maps, tokens, STL files or other copyrighted
        files are stored here, and links point to where a product is officially sold or given away.{' '}
        {wargames && 'Wargame factions are listed by name only: no stat lines, rules text or points values. '}Maps,
        tokens and miniatures are not catalogued at all, because even a preview image of one is most of what makes it
        worth having. Covers and previews are small images that identify a product. A link to an unofficial copy is
        removed; report one if you find it.
      </p>

      <h2 id="review">How edits are reviewed</h2>
      <p>Every change, including adding a record, is an edit with a note and, ideally, a source link.</p>
      <ul>
        <li>
          Edits from newer contributors wait in the review queue until an editor approves them. New records need a
          source link or a note saying where the information came from.
        </li>
        <li>
          Anyone signed in can vote. Enough approving votes, with no objections, apply an edit without waiting for an
          editor.
        </li>
        <li>
          Once you have enough edits applied, yours publish immediately, except renaming, moving or flagging a record,
          which is always reviewed.
        </li>
        <li>Save an edit as a draft to finish it later; nobody else sees a draft.</li>
        <li>
          A reviewer can fix a small mistake in your edit, like a typo or a missing author, before applying it. The edit
          stays yours, and its page shows what they changed.
        </li>
        <li>
          Deleting or merging a record needs a moderator. A merged record keeps working as a link to the one it was
          merged into.
        </li>
        <li>Nothing is lost: every record keeps its full history, and any earlier version can be restored.</li>
      </ul>
      <p>
        Two people can edit the same record at once. Edits touching different fields both apply; if someone changed the
        same field to something else in the meantime, the later edit is held back and has to be resubmitted.
      </p>

      <h2 id="standards">What good metadata looks like</h2>
      <ul>
        <li>
          Write descriptions in your own words. A sentence or two is fine; do not paste a publisher’s blurb wholesale.
        </li>
        <li>
          Search before adding. Printer-friendly cuts, form-fillable sheets and older printings are variants of one
          book, not new books.
        </li>
        <li>List every system a book supports, most important first.</li>
        <li>
          Partial is welcome: a title and a system beat no record. The gold frame around each cover shows what is still
          unknown.
        </li>
        <li>
          If something is wrong and you cannot fix it, report it. Reports go to moderators, and every decision is
          logged.
        </li>
      </ul>

      <h2 id="owners">Publishers and creators</h2>
      <p>
        If you are the publisher or creator a record describes, claim it from its page. An admin verifies every claim by
        hand. Verified owners’ edits to their own records apply immediately, and edits by others to those records wait
        for the owner (or an editor) to review them. The community can still correct an owner’s records; disagreements
        go to moderators.
      </p>

      <h2 id="books-and-systems">Books in more than one system</h2>
      <p>
        A book lists every system it supports: a dual-statted adventure appears on both systems’ shelves. The first
        system listed is the one it is primarily written for.
      </p>

      <h2 id="api">Using the API</h2>
      <p>
        Everything you can read here is available as JSON under <code>/api/v1</code>, without an account, and to AI
        assistants over MCP. The <Link to="/developers">API and MCP page</Link> has the full reference. Useful entry
        points:
      </p>
      <ul>
        <li>
          <code>GET /api/v1/search?q=mothership</code>
        </li>
        <li>
          <code>POST /api/v1/match</code> with whatever you know about a file: hashes, ISBN, title, file name, authors
        </li>
        <li>
          <code>GET /api/v1/books/lookup?isbn=…</code>, <code>?product_code=…</code> or{' '}
          <code>?hash=…&amp;algo=sha256</code>
        </li>
        <li>
          <code>GET /api/v1/lookup?source=drivethrurpg&amp;id=…</code>
        </li>
      </ul>
      <p>
        To submit edits from a script or a Grimoire server, create an API token on your{' '}
        <Link to="/account">account page</Link> and send it as <code>Authorization: Bearer cdx_…</code>. See the{' '}
        <Link to="/privacy">privacy page</Link> for what a Grimoire instance shares.
      </p>
    </ReadingPage>
  )
}

export function NotFound() {
  useTitle('Not found')
  return (
    <div className="page narrow">
      <h1 className="page-title">Nothing on this page</h1>
      <p className="lede">The link may be old, or the record was merged into another.</p>
      <p>
        <Link to="/" className="button button-quiet">
          Back to the catalogue
        </Link>
      </p>
    </div>
  )
}
